import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getTempUserId } from '../utils'
import Login from '../components/Login';
import { fetchPlayer, fetchGroup, login, addPlayerToGroup, addPlayer } from '../api';
import Header from '../components/Header';
import Footer from '../components/Footer';

/**
 * Invitation Page
 *
 * Landing page for received invitations.
 *
 * @returns {JSX.Element}
 * @constructor
 */
const Invitation = () => {
  const { playerFromId, groupGuid } = useParams();
  const [inviteFrom, setInviteFrom] = useState(null);
  const [group, setGroup] = useState(null);
  const [message, setMessage] = useState('Loading...');
  const [showLogin, setShowLogin] = useState(false);
  const [loginErrorMsg, setLoginErrorMsg] = useState('');

  /**
   * Validate params and fetch the group and player.
   */
  useEffect(() => {
    const player = JSON.parse(localStorage.getItem('player'));
    if (!player) {
      setShowLogin(true);
    } else {
      // Validate the invitee
      fetchPlayer(player._id || player.temp_id).then((player) => {
        if (!player) {
          // Invalid player in local storage
          localStorage.removeItem('player');
          window.location.reload();
        }
      });

      // Cannot accept own invitation
      if (playerFromId === player._id || playerFromId === player.temp_id) {
        window.location = '/dashboard';
        return;
      }
    }

    // Get the player the invitation is from
    if (inviteFrom === null) {
      fetchPlayer(playerFromId).then((player) => {
        if (player === null) {
          setMessage('Invalid invitation');
          return;
        }
        const fromName = player.name ?? player.email ?? 'Anonymous';
        setInviteFrom(fromName);
      });
    }

    // Get the group
    if (group === null) {
      fetchGroup(groupGuid).then((response) => {
        if (response.group === null) {
          setMessage('Invalid invitation');
          return;
        }
        setGroup(response.group);
      });
    }
  }, []);

  /**
   * onLogin
   *
   * Handler for the login button.
   */
  const onLogin = () => {
    const email = document.querySelector('#email').value;
    const password = document.querySelector('#password').value;

    if (email && password) {
      login(email, password).then((result) => {
        if (result.error) {
          setLoginErrorMsg(result.error);
        } else if (result.success) {
          localStorage.setItem('player', JSON.stringify(result.player));
          setShowLogin(false);
        } else {
          setLoginErrorMsg('Unknown error. Please try again.');
        }
      });
    } else {
      setLoginErrorMsg('Please enter an email and password.');
    }
  }

  /**
   * onAccept
   *
   * Handler for the accept button.
   */
  const onAccept = () => {
    const player = JSON.parse(localStorage.getItem('player'));
    if (player === null) {
      setShowLogin(true);
      return;
    }

    // Add player to the group
    addPlayerToGroup(player._id || player.temp_id, groupGuid).then((result) => {
      if (result.error) {
        // TODO: Show error message
        console.error('Error adding player to group: ', result.error);
      } else if (result.success) {
        // Go to question
        window.location.href = '/play/' + groupGuid;
      } else {
        // TODO: Show error message
        console.error('Unknown error. Please try again.');
      }
    });
  }

  /**
   * onContinueClick
   *
   * Handler for the continue without login button.
   *
   * @returns {Promise<void>}
   */
  const onContinueClick = async () => {
    // Create a temp player if one doesn't exist
    let player = JSON.parse(localStorage.getItem('player'));
    if (!player) {
      // Create a temp player
      player = {
        temp_id: getTempUserId(),
        groups: [groupGuid],
        stats: {
          questions_answered: 0,
          total_score: 0
        }
      };
    } else {
      player.groups.push(groupGuid);
    }

    // Store or update player in local storage
    localStorage.setItem('player', JSON.stringify(player));

    // Add player to the server
    const newPlayer = await addPlayer(player);
    if (newPlayer === null) {
      // TODO: Show error message
      return;
    }
    localStorage.setItem('player', JSON.stringify(newPlayer));

    // Redirect to the question
    window.location.href = '/play/' + groupGuid;
  }

  if (inviteFrom && group) {
    console.log(group.subject);
    return (
      <div className="page-wrap">
        <Header/>
        <div className="invitation-page">
          <div className="invitation-message">
            <span>You have been invited to a game of trivia by {inviteFrom}! The subject is <span className="group-subject">'{group.subject}'.</span></span><br/><br/>
          </div>
          {showLogin ?
            <div>
              <span>If you already have a login...</span>
              <Login callback={onLogin} errorMsg={loginErrorMsg} key={loginErrorMsg} showTitle={false}/>
              <div className="or">or</div>
              <button className="main-btn" onClick={onContinueClick}>Continue Without Login</button>
            </div>
            :
            <div>
            <button className="main-btn" onClick={onAccept}>Accept</button>
            </div>
          }
        </div>
        <Footer/>
      </div>
    )
  } else {
    return (
      <div className="invitation-page">
        <div className="invitation-message">{message}</div>
      </div>
    )
  }
}

export default Invitation
